import useSWR from 'swr'
import { Inventory, SailContent } from '@/root/shared-types'
import { defaultInventory } from '@/root/shared-consts'

interface UseInventoryForDateParams {
  date: string
  locale: string
  sailPackage: string
}

interface InventoryResponse {
  error: Error | undefined
  inventory: Inventory
  isLoading: boolean
  sailContent: SailContent[]
}

export const useInventoryForDate = ({ sailPackage, date, locale }: UseInventoryForDateParams): InventoryResponse => {
  const shouldFetch = Boolean(sailPackage && date && locale)
  const swrKey = shouldFetch ? `/api/inventory-and-sails/${sailPackage}/${date}?locale=${locale}` : null

  const { data, error, isLoading } = useSWR(
    swrKey,
    async () => {
      // todo: use apiAxios here instead of fetch
      const [inventoryRes, sailsRes] = await Promise.all([
        fetch(`/api/inventory/${sailPackage}/${date}`, {
          headers: { 'Accept-Language': locale },
        }),
        fetch(`/api/sails/${sailPackage}/${date}`, {
          method: 'POST',
        }),
      ])

      if (!inventoryRes.ok || !sailsRes.ok) {
        throw new Error('Failed to fetch inventory or sails')
      }

      const [inventory, sailContent] = await Promise.all([inventoryRes.json(), sailsRes.json()])

      return { inventory, sailContent }
    },
    {
      // swr/immutable is avoided here intentionally: inventory must be re-fetched on
      // mount so the user always sees fresh capacity data after returning to this tab.
      revalidateOnMount: true,
      revalidateIfStale: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )

  return {
    inventory: data?.inventory ?? defaultInventory,
    sailContent: data?.sailContent ?? [],
    isLoading: isLoading || !data,
    error,
  }
}
